import type { JSX, ReactNode } from "react";
import type { IconName } from "../components/ui/Icon";
import {
  IconActionButton,
  PanelHeader,
  PanelShell,
} from "../components/ui/PanelPrimitives";
import type { AppRuntime } from "../runtime/contracts/runtime";
import { ExpandableEntityCard } from "../components/agent/ExpandableEntityCard";

export interface ContextPanelEntity {
  readonly id: string;
  readonly schemaId: string;
  readonly name?: string;
  readonly linkKind?: string;
  readonly createdAt?: string;
  readonly data?: Readonly<Record<string, unknown>>;
}

interface ContextInfoItem {
  readonly iconName: IconName;
  readonly iconColor: string;
  readonly text: string;
}

export interface ClickableEntity {
  readonly id: string;
  readonly schemaId: string;
}

export interface ContextPanelProps {
  readonly primaryEntity?: ContextPanelEntity;
  readonly replyToEntity?: ContextPanelEntity;
  readonly infoItems: readonly ContextInfoItem[];
  readonly connectedEntities: readonly ContextPanelEntity[];
  readonly runtime: AppRuntime;
  /** When true, omit the PanelHeader (used when embedded in AgentPanel sidebar which has its own header). */
  readonly hideHeader?: boolean;
  /**
   * Optional region rendered BELOW the entity list, in the same scroll flow —
   * home of the always-visible Todo + Context-stats transparency region
   * (owner decision 2026-07-23: entities on top, Todo + stats beneath them,
   * in the right Context panel rather than inline above the composer).
   */
  readonly bottomSlot?: ReactNode;
}

export function ContextPanel({
  primaryEntity,
  replyToEntity,
  infoItems: _infoItems,
  connectedEntities,
  runtime,
  hideHeader,
  bottomSlot,
}: ContextPanelProps): JSX.Element {
  // Flat list, deduped by id. Context panel is an identifier list — the
  // "what the agent did" details live in the chat card (immutable
  // envelope), not duplicated here.
  const seen = new Set<string>();
  const ordered: ContextPanelEntity[] = [];
  const push = (e: ContextPanelEntity | undefined): void => {
    if (!e || seen.has(e.id)) return;
    seen.add(e.id);
    ordered.push(e);
  };
  push(primaryEntity);
  push(replyToEntity);
  for (const e of connectedEntities) push(e);

  return (
    <PanelShell className="bg-transparent">
      {!hideHeader && (
        <PanelHeader
          title="Context"
          className="px-4 py-3.5"
          titleClassName="text-sm font-semibold text-agent-text-heading"
          action={
            <IconActionButton
              iconName="ellipsis-vertical"
              iconSize={18}
              className="text-agent-text-muted hover:text-agent-text"
            />
          }
        />
      )}

      {/* Two regions, not one scroll flow: the entity list takes the space that
          is left and scrolls inside it, the Todo + stats region keeps its height
          at the bottom. `min-h-0` on both the column and the scroller is what
          lets the scroller actually shrink — a flex child's default min-height
          is its content, so without it the column grows past the panel and the
          bottom region is pushed off screen. */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
          {ordered.map((entity) => (
            <ExpandableEntityCard
              key={entity.id}
              schemaId={entity.schemaId}
              data={{
                id: entity.id,
                name: entity.name,
                link_kind: entity.linkKind,
                created_at: entity.createdAt,
                ...entity.data,
              }}
              runtime={runtime}
            />
          ))}
        </div>
        {/* Todo + Context-stats region — always visible at the bottom.
            @tested-by: tst_fe_ctxrender_003, tst_fe_ctxrender_004
            `shrink-0` because a flex child shrinks by default, which turned
            "always visible" into a sliver once the entity list grew. It carries
            its own cap and scroll so a long todo list cannot squeeze the
            entities to nothing either. */}
        {bottomSlot != null && (
          // No scroll HERE: the Todo block scrolls its own rows and keeps its
          // header. Scrolling the whole slot took the header with it.
          <div className="flex shrink-0 max-h-[55%] flex-col overflow-hidden border-t border-agent-border pb-2">
            {bottomSlot}
          </div>
        )}
      </div>
    </PanelShell>
  );
}
